import type { Tone } from '@wl/ui';
import type { AccountStatus, AccountStatusReason, MarketStatus, Num, OrderSource, OrderStatus, OrderType, SessionStatus, Side } from './invest-types';

export const ORDER_STATUS: Record<OrderStatus, { label: string; tone: Tone }> = {
  submitted: { label: 'Gönderildi', tone: 'neutral' },
  accepted: { label: 'İletildi', tone: 'primary' },
  partially_filled: { label: 'Kısmi gerçekleşti', tone: 'warning' },
  filled: { label: 'Gerçekleşti', tone: 'success' },
  cancelled: { label: 'İptal', tone: 'neutral' },
  rejected: { label: 'Reddedildi', tone: 'danger' },
};

export const OPEN_ORDER_STATUSES: OrderStatus[] = ['submitted', 'accepted', 'partially_filled'];
export const isOpenOrder = (s: OrderStatus) => OPEN_ORDER_STATUSES.includes(s);

export const SIDE_LABEL: Record<Side, string> = { buy: 'Alış', sell: 'Satış' };
export const ORDER_TYPE_LABEL: Record<OrderType, string> = { market: 'Piyasa', limit: 'Limit' };
export const ORDER_SOURCE_LABEL: Record<OrderSource, string> = {
  manual: 'Manuel',
  admin: 'Yetkili',
  bulk: 'Toplu emir',
  auto: 'Robot',
  stop: 'Stop',
};

export const SESSION_STATUS: Record<SessionStatus, { label: string; tone: Tone }> = {
  connected: { label: 'Bağlı', tone: 'success' },
  awaiting_otp: { label: 'Kod bekleniyor', tone: 'warning' },
  disconnected: { label: 'Bağlı değil', tone: 'neutral' },
  expired: { label: 'Oturum düştü', tone: 'warning' },
  failed: { label: 'Giriş başarısız', tone: 'danger' },
};

export const ACCOUNT_STATUS: Record<AccountStatus, { label: string; tone: Tone }> = {
  active: { label: 'Aktif', tone: 'success' },
  inactive: { label: 'Pasif', tone: 'neutral' },
  suspended: { label: 'Askıda', tone: 'danger' },
};

export const ACCOUNT_STATUS_REASON: Record<AccountStatusReason, string> = {
  subscription_expired: 'Abonelik sona erdi',
  plan_limit_exceeded: 'Paket limiti aşıldı',
  admin: 'Yönetici tarafından',
  user: 'Kullanıcı tarafından',
};

const CLOSED_REASON: Record<NonNullable<MarketStatus['reason']>, string> = {
  weekend: 'Hafta sonu',
  holiday: 'Resmî tatil',
  before_open: 'Seans henüz açılmadı',
  after_close: 'Seans kapandı',
  no_session: 'Seans tanımı yok',
};

export function marketStatusText(s: MarketStatus): string {
  const hours = s.opensAt && s.closesAt ? `${s.opensAt.slice(0, 5)}–${s.closesAt.slice(0, 5)}` : null;
  if (s.open) return `Seans açık${hours ? ` (${hours})` : ''}${s.dayType === 'half_day' ? ' · yarım gün' : ''}`;
  const reason = s.reason ? CLOSED_REASON[s.reason] : 'Seans kapalı';
  const holiday = s.holiday ? ` · ${s.holiday}` : '';
  const opens = s.reason === 'before_open' && s.opensAt ? ` · ${s.opensAt.slice(0, 5)}'te açılır` : '';
  return `${reason}${holiday}${opens}`;
}

/** Numeric alanlar metin olarak gelebilir. */
export function toNum(v: Num | undefined): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Kullanıcı girdisini sayıya çevirir: "12,5", "1.234,56", "1234.56" kabul edilir.
 * Geçersiz ya da boşsa null.
 */
export function parseDecimal(input: string): number | null {
  const text = input.trim().replace(/\s/g, '');
  if (!text) return null;
  let normalized: string;
  if (text.includes(',')) {
    // Virgül ondalık ayırıcı; noktalar binlik.
    normalized = text.replace(/\./g, '').replace(',', '.');
  } else if ((text.match(/\./g) ?? []).length > 1) {
    normalized = text.replace(/\./g, '');
  } else {
    normalized = text;
  }
  if (!/^-?\d+(\.\d+)?$/.test(normalized)) return null;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

/** Pozitif tam sayı ("1.000" binlik ayırıcıyla da); değilse null. */
export function parseQuantity(input: string): number | null {
  const text = input.trim().replace(/\s/g, '');
  if (!/^\d+$/.test(text) && !/^\d{1,3}(\.\d{3})+$/.test(text)) return null;
  const n = Number(text.replace(/\./g, ''));
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

/** Sembolleri büyük harfe çevirir, tekrarları ve geçersizleri atar, sınırı uygular. */
export function normalizeCodes(codes: readonly string[], max: number): string[] {
  const out: string[] = [];
  for (const c of codes) {
    const code = c.trim().toUpperCase();
    if (!/^[A-Z0-9._-]{1,20}$/.test(code) || out.includes(code)) continue;
    out.push(code);
    if (out.length >= max) break;
  }
  return out;
}

export function accountTitle(a: { label: string | null; broker: { name: string } | string; identity?: string | null }): string {
  const broker = typeof a.broker === 'string' ? a.broker : a.broker.name;
  if (a.label) return a.label;
  return a.identity ? `${broker} · ${a.identity}` : broker;
}

export function personName(u: { firstName: string; lastName: string } | null | undefined): string {
  if (!u) return '—';
  return `${u.firstName} ${u.lastName}`.trim() || '—';
}

/** Fiyat adımına göre basamak: BIST fiyatları 2 ondalık, çok küçükler 4. */
export function priceDigits(v: number | null | undefined): number {
  return v !== null && v !== undefined && Math.abs(v) > 0 && Math.abs(v) < 1 ? 4 : 2;
}

/** "YYYY-MM-DD" (yerel gün) → o günün başlangıcı (ISO); `nextDay` ise ertesi günün. */
export function dayBoundary(day: string, nextDay = false): string | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return undefined;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + (nextDay ? 1 : 0));
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

const compact = new Intl.NumberFormat('tr-TR', { notation: 'compact', maximumFractionDigits: 1 });

/** Hacim gibi büyük sayılar: 1234567 → "1,2 Mn". */
export function formatCompact(v: number | null | undefined): string {
  return v === null || v === undefined || !Number.isFinite(v) ? '—' : compact.format(v);
}

/** Son fiyatın gün içi aralıktaki yeri (0–1); hesaplanamazsa null. */
export function rangePosition(last: number | null | undefined, low: number | null | undefined, high: number | null | undefined): number | null {
  if (last == null || low == null || high == null || high < low) return null;
  if (high === low) return 0.5;
  return Math.min(1, Math.max(0, (last - low) / (high - low)));
}

/** Yüzde girdisini orana çevirir ("2,5" → 0.025). */
export function percentToRatio(input: string): number | null {
  const n = parseDecimal(input);
  return n === null ? null : n / 100;
}
